import React from 'react';
import {CircularProgress, Box,} from "@mui/joy";
import dayjs from "dayjs";

// components
import BaseApi from "../../utils/BaseApi";
import withRouter from "../../utils/withRouter";
import CommonPaginatedTable from "../Common/CommonPaginatedTable";
import CommonActionModal from "../Common/CommonActionModal";
import CommonForm from "../Common/CommonForm";
import retrievePermission from "../../utils/retrievePermission";
import UserSubscriberContext from "../../context/UserSubscriberContext";
import CommonCallBackButton from "../Common/CommonCallBackButton";

// icons
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';


class BonusFormModal extends CommonActionModal {
    async componentDidMount() {
        this.setState({
            buttonIcon: this.props.componentObj?.id ? EditIcon : AddCircleIcon,
            buttonLabel: this.props.componentObj?.id ? 'Редактировать' : 'Добавить',
            ready: true,
        })
    }

    async beforeModalOpen() {
        const api = new BaseApi();
        let fields = await api.get('employee_bonus/update_form/', {
            fields: JSON.stringify([
                'salary',
                'pay_date',
                'description',
                'approved',
                'payed',
            ])
        });
        
        return {
            modalLabel: this.props.componentObj?.id ? 'Редактирование премии' : 'Создание премии',
            modalIcon: AttachMoneyIcon,
            mainComponent: CommonForm,
            mainComponentProps: {
                fields: fields.data,
                processForm: this.processForm.bind(this),
            }
        };
    }

    async processForm(form) {
        const api = new BaseApi();
        const bonus = this.props.componentObj;
        let formDataObj = Object.fromEntries(
            form.state.formData.entries()
        );
        
        if (bonus) {
            await api.patch(`employee_bonus/${bonus.id}/`, formDataObj);
        } else {
            formDataObj.employee = this.props.employee.id;
            await api.post('employee_bonus/', formDataObj);
        }

        this.setModalOpenFalse();
        if (this.props.callBack) this.props.callBack();
    }
}


class EmployeeDetailBonusList extends React.Component {
    static contextType = UserSubscriberContext

    constructor(props) {
        super(props);
        this.state = {
            bonuses: null,
            pages: null,
            loading: false,
            updatePermission: 0,
            createPermission: 0,
            deletePermission: 0,
        };
    }

    async componentDidMount() {
        await this.fetchBonuses();
        await this.retrievePermission();
    }

    async retrievePermission() {
        let updatePermission = await retrievePermission(
            'EmployeeBonusViewSet',
            'partial_update',
            this.context.subscriberObject
        );
        let createPermission = await retrievePermission(
            'EmployeeBonusViewSet',
            'create',
            this.context.subscriberObject
        );
        let deletePermission = await retrievePermission(
            'EmployeeBonusViewSet',
            'destroy',
            this.context.subscriberObject
        );
        await this.setState({
            updatePermission,
            createPermission,
            deletePermission,
        });
    }

    async componentDidUpdate(prevProps) {
        if (prevProps.employee.id !== this.props.employee.id) {
            await this.fetchBonuses();
        }
    }

    async fetchBonuses() {
        this.setState({loading: true});
        const url = 'employee_bonus/';
        const api = new BaseApi();
        try {
            const res = await api.get(url, {
                employee: this.props.employee.id,
            });
            await this.setState({
                bonuses: res.data.results || [],
                pages: res.data.total_pages || 1,
                loading: false,
            });
        } catch (e) {
            console.error("Failed to fetch bonuses", e);
            await this.setState({bonuses: [], pages: 1, loading: false});
        }
    }

    async deleteBonus(id) {
        const api = new BaseApi();
        await api.delete(`employee_bonus/${id}/`);
        await this.fetchBonuses();
    }

    render() {
        if (this.state.loading) return <CircularProgress />;
        if (!this.state.bonuses || !this.state.pages) {
            return <CircularProgress />;
        }

        return (
            <Box sx={{width: '100%'}}>
                <CommonPaginatedTable
                    title="Премии"
                    url={'employee_bonus'}
                    data={this.state.bonuses}
                    pages={this.state.pages}
                    callBack={this.fetchBonuses.bind(this)}
                    extraComponents={
                        this.state.createPermission ? [
                            {
                                component: BonusFormModal,
                                componentProps: {
                                    buttonIcon: AddCircleIcon,
                                    buttonLabel: 'Добавить премию',
                                    flat: false,
                                    overlay: false,
                                    employee: this.props.employee,
                                },
                                callBack: this.fetchBonuses.bind(this),
                            }
                        ] : []
                    }
                    fields={[
                        {
                            title: 'Дата выплаты',
                            name: 'pay_date',
                            visible: true,
                            preProcess: (v) => v ? dayjs(v).format('DD.MM.YYYY') : 'Не указана',
                            edit: {
                                permission: true,
                                fields: ['pay_date', ],
                                baseUrl: 'employee_bonus',
                            },
                        },
                        {
                            title: 'Сумма',
                            name: 'salary',
                            visible: true,
                            edit: {
                                permission: true,
                                fields: ['salary', ],
                                baseUrl: 'employee_bonus',
                            },
                        },
                        {
                            title: 'Согласовано',
                            name: 'approved',
                            visible: true,
                            edit: {
                                permission: true,
                                fields: ['approved', ],
                                baseUrl: 'employee_bonus',
                            },
                        },
                        {
                            title: 'Выплачено',
                            name: 'payed',
                            visible: true,
                            edit: {
                                permission: true,
                                fields: ['payed', ],
                                baseUrl: 'employee_bonus',
                            },
                        },
                        {
                            title: 'Сделка',
                            name: 'opportunity.short_name',
                            visible: true,
                            edit: {
                                permission: true,
                                fields: ['opportunity', ],
                                baseUrl: 'employee_bonus',
                                obj: {
                                    id: 'id',
                                    opportunity: 'opportunity.id'
                                }
                            },
                        },
                        {
                            title: 'Комментарий',
                            name: 'description',
                            visible: true,
                            preProcess: (value) => (value || 'Не указано'),
                            edit: {
                                permission: true,
                                fields: ['description', ],
                                baseUrl: 'employee_bonus',
                            },
                        },
                        {
                            title: '',
                            visible: this.state.deletePermission,
                            component: CommonCallBackButton,
                            componentProps: {
                                propsObject: 'componentObj',
                                propsObjectKey: 'id',
                                icon: DeleteIcon,
                                color: 'danger',
                            },
                            callBack: this.deleteBonus.bind(this),
                        }
                    ]}
                />
            </Box>
        );
    }
}

export default withRouter(EmployeeDetailBonusList);
